// ─── src/pages/PressPage.jsx ──────────────────────────────────────────────────
// /press, Entity anchor page for LLM recognition and E-E-A-T.
//
// Purpose:
//   Signals to search engines and AI systems that Luxury Wedding Directory is
//   a legitimate, established organisation with a documented public presence.
//   Even sparse at launch, the page establishes the entity and invites citation.
//
// Schema: Organization (entity anchor) + BreadcrumbList
// ─────────────────────────────────────────────────────────────────────────────
import { Helmet } from 'react-helmet-async';
import { useBreakpoint } from '../hooks/useWindowWidth';
import HomeNav from '../components/nav/HomeNav';

const SITE_URL = 'https://luxuryweddingdirectory.com';
const GOLD     = '#C4A35A';
const GD       = 'var(--font-heading-primary)';
const NU       = 'var(--font-body)';

// ── Press coverage entries ────────────────────────────────────────────────────
// Add entries as coverage is confirmed. Each: { outlet, title, url, year }
// Leave empty to show the "coverage in progress" state.
const PRESS_MENTIONS = [
];

// ── Schema ────────────────────────────────────────────────────────────────────
function buildSchema() {
  const org = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': `${SITE_URL}/#organization`,
    'name': 'Luxury Wedding Directory',
    'url': SITE_URL,
    'logo': `${SITE_URL}/og-default.jpg`,
    'description': 'Luxury Wedding Directory is a curated directory of exceptional wedding venues and vendors across Europe, covering Italy, France, Spain, England and 11 further countries. Published by 5 Star Weddings Ltd, established in 2006.',
    'foundingDate': '2008',
    'parentOrganization': {
      '@type': 'Organization',
      'name': '5 Star Weddings Ltd',
      'foundingDate': '2006',
      'description': 'Publisher of 5 Star Wedding Directory, the world\'s first luxury wedding directory, established 2006.',
    },
    'areaServed': [
      'Italy', 'France', 'Spain', 'England', 'Scotland', 'Ireland',
      'Portugal', 'Greece', 'Croatia', 'Switzerland', 'Austria',
      'Germany', 'Netherlands', 'Belgium',
    ],
    'sameAs': [
      'https://www.5starweddingdirectory.com',
    ],
    'contactPoint': {
      '@type': 'ContactPoint',
      'contactType': 'Press enquiries',
      'email': 'press@luxuryweddingdirectory.com',
    },
  };

  const breadcrumb = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    'itemListElement': [
      { '@type': 'ListItem', 'position': 1, 'name': 'Home', 'item': SITE_URL },
      { '@type': 'ListItem', 'position': 2, 'name': 'Press', 'item': `${SITE_URL}/press` },
    ],
  };

  return [org, breadcrumb];
}

// ── Divider ───────────────────────────────────────────────────────────────────
function GoldRule({ mt = 0, mb = 0 }) {
  return (
    <div aria-hidden style={{
      width: 40, height: 1,
      background: GOLD, opacity: 0.5,
      margin: `${mt}px 0 ${mb}px`,
    }} />
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function PressPage({ onBack }) {
  const { isMobile } = useBreakpoint();
  const schemas = buildSchema();

  return (
    <>
      <Helmet>
        <title>Press | Luxury Wedding Directory</title>
        <meta
          name="description"
          content="Press information for Luxury Wedding Directory, a curated European wedding venue and vendor directory published by 5 Star Weddings Ltd."
        />
        <link rel="canonical" href={`${SITE_URL}/press`} />
        <meta property="og:title"       content="Press | Luxury Wedding Directory" />
        <meta property="og:description" content="Press information, media contact and coverage for Luxury Wedding Directory." />
        <meta property="og:url"         content={`${SITE_URL}/press`} />
        <meta property="og:type"        content="website" />
        {schemas.map((s, i) => (
          <script key={i} type="application/ld+json">{JSON.stringify(s)}</script>
        ))}
      </Helmet>

      <div style={{
        minHeight: '100vh',
        background: '#FAF8F4',
        fontFamily: NU,
      }}>
        {/* Global navigation, master logo + menu, so Press reads as part of the site */}
        <HomeNav lightScheme hasHero={false} sticky onToggleDark={() => {}} />

        {/* ── Hero ──────────────────────────────────────────────────────────── */}
        <div style={{
          borderBottom: '1px solid rgba(196,163,90,0.2)',
          padding: isMobile ? '48px 24px 56px' : '64px 48px 72px',
          maxWidth: 960,
          margin: '0 auto',
        }}>
          <p style={{
            fontFamily: NU, fontSize: 10, fontWeight: 600,
            letterSpacing: '0.28em', textTransform: 'uppercase',
            color: GOLD, margin: '0 0 24px',
          }}>
            Press
          </p>
          <h1 style={{
            fontFamily: GD, fontSize: isMobile ? 36 : 52,
            fontWeight: 400, color: '#2a1f0e',
            lineHeight: 1.15, letterSpacing: '-0.01em',
            margin: '0 0 24px', maxWidth: 600,
          }}>
            Media Information
          </h1>
          <p style={{
            fontFamily: NU, fontSize: 15, color: 'rgba(42,31,14,0.65)',
            lineHeight: 1.8, margin: 0, maxWidth: 540,
          }}>
            For press enquiries, interview requests, or editorial partnerships,
            contact us directly below.
          </p>
        </div>

        {/* ── About section ─────────────────────────────────────────────────── */}
        <section style={{
          maxWidth: 960,
          margin: '0 auto',
          padding: isMobile ? '56px 24px' : '72px 48px',
          borderBottom: '1px solid rgba(196,163,90,0.15)',
        }}>
          <p style={{
            fontFamily: NU, fontSize: 10, fontWeight: 600,
            letterSpacing: '0.28em', textTransform: 'uppercase',
            color: GOLD, margin: '0 0 32px',
          }}>
            About
          </p>

          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
            gap: isMobile ? 32 : 64,
          }}>
            <div>
              <p style={{
                fontFamily: NU, fontSize: 15, color: '#2a1f0e',
                lineHeight: 1.85, fontWeight: 300, margin: '0 0 20px',
              }}>
                Luxury Wedding Directory is a curated directory of exceptional
                wedding venues and vendors across Europe, covering Italy, France,
                Spain, England and 11 further countries. It is published by
                5 Star Weddings Ltd, a luxury wedding media company established
                in 2006.
              </p>
              <p style={{
                fontFamily: NU, fontSize: 15, color: '#2a1f0e',
                lineHeight: 1.85, fontWeight: 300, margin: '0 0 20px',
              }}>
                The directory serves couples planning high-end weddings, with
                AI-powered discovery through Aura, editorial content through
                LWD Magazine, and a vetted collection of venues and creative
                professionals. Every listing is reviewed by the editorial team.
                Placement cannot be purchased.
              </p>
              <p style={{
                fontFamily: NU, fontSize: 15, color: '#2a1f0e',
                lineHeight: 1.85, fontWeight: 300, margin: 0,
              }}>
                The domain has been active since 2008. 5 Star Weddings Ltd
                also operates 5 Star Wedding Directory, the world's first
                luxury wedding directory, established in 2006.
              </p>
            </div>

            <div>
              <div style={{ marginBottom: 32 }}>
                <p style={{
                  fontFamily: NU, fontSize: 10, fontWeight: 600,
                  letterSpacing: '0.22em', textTransform: 'uppercase',
                  color: 'rgba(42,31,14,0.45)', margin: '0 0 10px',
                }}>
                  Founded
                </p>
                <p style={{
                  fontFamily: GD, fontSize: 22, color: '#2a1f0e',
                  margin: 0, fontWeight: 400,
                }}>
                  2006
                </p>
                <p style={{
                  fontFamily: NU, fontSize: 13, color: 'rgba(42,31,14,0.55)',
                  margin: '4px 0 0', lineHeight: 1.6,
                }}>
                  5 Star Weddings Ltd
                </p>
              </div>

              <div style={{ marginBottom: 32 }}>
                <p style={{
                  fontFamily: NU, fontSize: 10, fontWeight: 600,
                  letterSpacing: '0.22em', textTransform: 'uppercase',
                  color: 'rgba(42,31,14,0.45)', margin: '0 0 10px',
                }}>
                  Coverage
                </p>
                <p style={{
                  fontFamily: GD, fontSize: 22, color: '#2a1f0e',
                  margin: 0, fontWeight: 400,
                }}>
                  14 countries
                </p>
                <p style={{
                  fontFamily: NU, fontSize: 13, color: 'rgba(42,31,14,0.55)',
                  margin: '4px 0 0', lineHeight: 1.6,
                }}>
                  Across Europe
                </p>
              </div>

              <div>
                <p style={{
                  fontFamily: NU, fontSize: 10, fontWeight: 600,
                  letterSpacing: '0.22em', textTransform: 'uppercase',
                  color: 'rgba(42,31,14,0.45)', margin: '0 0 10px',
                }}>
                  Domain active since
                </p>
                <p style={{
                  fontFamily: GD, fontSize: 22, color: '#2a1f0e',
                  margin: 0, fontWeight: 400,
                }}>
                  2008
                </p>
                <p style={{
                  fontFamily: NU, fontSize: 13, color: 'rgba(42,31,14,0.55)',
                  margin: '4px 0 0', lineHeight: 1.6,
                }}>
                  luxuryweddingdirectory.com
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ── Coverage ──────────────────────────────────────────────────────── */}
        <section style={{
          maxWidth: 960,
          margin: '0 auto',
          padding: isMobile ? '56px 24px' : '72px 48px',
          borderBottom: '1px solid rgba(196,163,90,0.15)',
        }}>
          <p style={{
            fontFamily: NU, fontSize: 10, fontWeight: 600,
            letterSpacing: '0.28em', textTransform: 'uppercase',
            color: GOLD, margin: '0 0 32px',
          }}>
            Coverage
          </p>

          {PRESS_MENTIONS.length === 0 ? (
            <p style={{
              fontFamily: NU, fontSize: 14, color: 'rgba(42,31,14,0.45)',
              lineHeight: 1.8, margin: 0, fontStyle: 'italic',
            }}>
              Coverage log in progress. Verified mentions will be listed here.
            </p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
              {PRESS_MENTIONS.map((item, i) => (
                <a
                  key={i}
                  href={item.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'baseline',
                    gap: 24,
                    padding: '20px 0',
                    borderBottom: '1px solid rgba(196,163,90,0.12)',
                    textDecoration: 'none',
                  }}
                >
                  <span style={{
                    fontFamily: NU, fontSize: 14, color: '#2a1f0e',
                    lineHeight: 1.5, fontWeight: 400,
                  }}>
                    {item.title}
                  </span>
                  <span style={{
                    fontFamily: NU, fontSize: 12, color: GOLD,
                    fontWeight: 600, letterSpacing: '0.06em',
                    whiteSpace: 'nowrap', flexShrink: 0,
                  }}>
                    {item.outlet} {item.year}
                  </span>
                </a>
              ))}
            </div>
          )}
        </section>

        {/* ── Media contact ─────────────────────────────────────────────────── */}
        <section style={{
          maxWidth: 960,
          margin: '0 auto',
          padding: isMobile ? '56px 24px 80px' : '72px 48px 100px',
        }}>
          <p style={{
            fontFamily: NU, fontSize: 10, fontWeight: 600,
            letterSpacing: '0.28em', textTransform: 'uppercase',
            color: GOLD, margin: '0 0 32px',
          }}>
            Media Contact
          </p>

          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
            gap: isMobile ? 32 : 64,
          }}>
            <div>
              <p style={{
                fontFamily: NU, fontSize: 15, color: '#2a1f0e',
                lineHeight: 1.85, fontWeight: 300, margin: '0 0 24px',
              }}>
                For press enquiries, interview requests, features, or editorial
                partnerships, please contact the team directly.
              </p>
              <a
                href="mailto:press@luxuryweddingdirectory.com"
                style={{
                  display: 'inline-block',
                  fontFamily: NU, fontSize: 13, fontWeight: 600,
                  letterSpacing: '0.1em', textTransform: 'uppercase',
                  color: GOLD, textDecoration: 'none',
                  borderBottom: `1px solid ${GOLD}`,
                  paddingBottom: 2,
                }}
              >
                press@luxuryweddingdirectory.com
              </a>
            </div>

            <div>
              <p style={{
                fontFamily: NU, fontSize: 10, fontWeight: 600,
                letterSpacing: '0.22em', textTransform: 'uppercase',
                color: 'rgba(42,31,14,0.45)', margin: '0 0 12px',
              }}>
                Brand assets
              </p>
              <p style={{
                fontFamily: NU, fontSize: 14, color: 'rgba(42,31,14,0.65)',
                lineHeight: 1.75, margin: 0,
              }}>
                High-resolution logos and brand guidelines are available on
                request. Contact the press address above.
              </p>
            </div>
          </div>

          <GoldRule mt={56} />

          <button
            onClick={onBack}
            style={{
              marginTop: 32,
              background: 'none', border: 'none', cursor: 'pointer', padding: 0,
              fontFamily: NU, fontSize: 11, fontWeight: 600,
              letterSpacing: '0.18em', textTransform: 'uppercase',
              color: 'rgba(42,31,14,0.4)',
              display: 'flex', alignItems: 'center', gap: 8,
            }}
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M8.75 2.625L4.375 7l4.375 4.375" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            Back
          </button>
        </section>
      </div>
    </>
  );
}
